import {
  useCreateMutation,
  useCustomMutation,
  useCustomQuery,
  useDeleteMutation,
  useFindByQuery,
  useInfiniteFindByQuery,
  useUpdateMutation,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';

const MODEL_NAME = 'campaign';

// useCreateMutation and useUpdateMutation each call invalidateQueries on
// success automatically (they use the factory's built-in cache key). The
// helper below is still needed by useDispatchCampaign, which goes through
// useCustomMutation because dispatch is still nested under Mutation.campaign.
const useInvalidateCampaignCache = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [MODEL_NAME] });
};

// ---------------------------------------------------------------------------
// GraphQL operation strings
// ---------------------------------------------------------------------------

const LIST_CAMPAIGNS = `
  query ListCampaigns($filter: CampaignsFilter!, $page: PageRequest, $orderBy: OrderByInput) {
    campaign {
      listCampaigns(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          title
          description
          status
          groupIds
          targetedAssetIds
          scheduledSendAt
          sentAt
          createdAt
          updatedAt
          replyCount
        }
      }
    }
  }
`;

const GET_CAMPAIGN = `
  query GetCampaign($id: ID!) {
    campaign {
      getCampaign(id: $id) {
        id
        title
        description
        status
        groupIds
        mediaIds
        targetedAssetIds
        scheduledSendAt
        sentAt
        sentBy
        createdBy
        createdAt
        updatedAt
        replyCount
        assetCount
      }
    }
  }
`;

const CREATE_CAMPAIGN = `
  mutation CreateCampaign($input: CreateCampaignInput!) {
    createCampaign(input: $input) {
      id
      title
      status
    }
  }
`;

const UPDATE_CAMPAIGN = `
  mutation UpdateCampaign($input: MutateCampaignInput!) {
    updateCampaign(input: $input) {
      id
      title
      status
    }
  }
`;

const DELETE_CAMPAIGN = `
  mutation DeleteCampaign($input: MutateCampaignInput!) {
    deleteCampaign(input: $input) {
      id
    }
  }
`;

const DISPATCH_CAMPAIGN = `
  mutation DispatchCampaign($id: ID!) {
    campaign {
      dispatchCampaign(id: $id) {
        success
        alreadySent
        message
        issuedCount
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Hook types
// ---------------------------------------------------------------------------

export type CampaignsFilter = {
  workspaceId: string;
  status?: string;
  keyword?: string;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Paginated (infinite scroll) list of campaigns.
 * Pass a CampaignsFilter with at least workspaceId.
 */
export const useListCampaigns = (filter: CampaignsFilter) =>
  useInfiniteFindByQuery({
    query: LIST_CAMPAIGNS,
    modelName: MODEL_NAME,
    dataPrefix: 'campaign.listCampaigns',
    filter,
    pageSize: 50,
  });

/**
 * Non-paginated campaigns query — use for small result sets or filters.
 */
export const useGetCampaignsList = (filter: CampaignsFilter) =>
  useFindByQuery({
    query: LIST_CAMPAIGNS,
    modelName: MODEL_NAME,
    variables: {
      filter,
    },
  });

/**
 * Fetch a single campaign by id.
 */
const FIND_MEDIA_BY_IDS = `
  query FindMediaByIds($filter: MediaFilter) {
    findMedia(filter: $filter) {
      items {
        id
        name
        type
        extension
        status
        url
      }
    }
  }
`;

/**
 * Fetch a set of Media records by their IDs in one round-trip.
 * Used by the edit-campaign panel to hydrate thumbnails for already-attached
 * media (Campaign schema only stores mediaIds, not the full Media records).
 */
export const useFindMediaByIds = (ids: string[]) =>
  useCustomQuery({
    query: FIND_MEDIA_BY_IDS,
    variables: { filter: { id: { in: ids } } },
    cacheKey: ['media', 'findByIds', ...ids],
    options: { enabled: ids.length > 0 },
  });

/**
 * Get a single campaign by id.
 *
 * Uses useCustomQuery rather than useGetQuery because our schema nests under
 * `campaign { getCampaign }` — useGetQuery's getDataObject unwrap only looks
 * at top-level keys and would return null. Callers read the raw nested shape
 * `data.campaign.getCampaign`.
 */
export const useGetCampaign = (id: string) =>
  useCustomQuery({
    query: GET_CAMPAIGN,
    variables: { id },
    cacheKey: ['campaign', 'get', id],
    options: { enabled: !!id },
  });

/**
 * Create a new campaign.
 *
 * Uses `useCreateMutation` — the mutation is now a top-level Graphback
 * operation (`createCampaign`), so the factory's `getDataObject` unwrap
 * finds it correctly at the response root. Cache invalidation is handled
 * automatically by the factory. Variables shape: `{ input: CreateCampaignInput }`.
 */
export const useCreateCampaign = () =>
  useCreateMutation({
    query: CREATE_CAMPAIGN,
    modelName: MODEL_NAME,
  });

/**
 * Update an existing campaign.
 *
 * Uses `useUpdateMutation` — top-level `updateCampaign` operation. The id
 * lives inside `input` (not as a separate variable) per the new
 * `MutateCampaignInput` shape. Cache invalidation is handled automatically.
 * Variables shape: `{ input: { id, ...fields } }`.
 */
export const useUpdateCampaign = () =>
  useUpdateMutation({
    query: UPDATE_CAMPAIGN,
    modelName: MODEL_NAME,
  });

/**
 * Delete a campaign.
 *
 * Uses `useDeleteMutation` — the new `deleteCampaign` operation takes
 * `input: MutateCampaignInput` (i.e. `{ input: { id } }`), which matches
 * the factory's `DeleteMutationVariables` shape exactly.
 */
export const useDeleteCampaign = () =>
  useDeleteMutation({
    query: DELETE_CAMPAIGN,
    modelName: MODEL_NAME,
  });

/**
 * Dispatch (send) a campaign immediately.
 *
 * Invalidates the campaign cache on success so the list reflects the post-send
 * status (DRAFT → SENT). Schedule goes through useUpdateCampaign which already
 * invalidates; dispatch needs its own because it changes server state after
 * the preceding update's invalidation has already fired.
 */
export const useDispatchCampaign = () => {
  const invalidate = useInvalidateCampaignCache();
  return useCustomMutation({
    query: DISPATCH_CAMPAIGN,
    options: { onSuccess: invalidate },
  });
};
